import React from 'react';
import PropTypes from 'prop-types';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import PublishIcon from '@mui/icons-material/Publish';
import UnpublishedIcon from '@mui/icons-material/Unpublished';
import { formatDateTime } from 'helpers/formaters';
import { formatSize, packageStatus, packageStatusColor } from 'common/packages';

const Field = ({ label, children }) => (
  <Grid item xs={12} sm={6} md={3}>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body1" sx={{ wordBreak: 'break-word' }}>
      {children || '—'}
    </Typography>
  </Grid>
);

const Preview = ({ label, uri }) => (
  <Box sx={{ textAlign: 'center' }}>
    <Typography variant="body2" color="text.secondary" gutterBottom>
      {label}
    </Typography>
    {uri ? (
      // The presigned URI goes straight into the img tag: the browser fetches it
      // without our Authorization header, which S3 would reject anyway.
      <Box
        component="img"
        src={uri}
        alt={label}
        sx={{ maxWidth: 120, maxHeight: 120, borderRadius: 1, border: '1px solid #eee' }}
      />
    ) : (
      <Typography variant="body2" color="text.disabled">
        none
      </Typography>
    )}
  </Box>
);

// Managing a package and downloading it are separate rights: content needs a
// licence AND a grant, for a package manager too. When the download will fail,
// this says which half is missing, because only the grant is fixable from here.
const DownloadDiagnosis = (props) => {
  const { application, position, email, granting, onSelfGrant } = props;

  if (!position) return null;

  if (position.known === false) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        Could not check whether this account may download this package. Treat the
        Download button as unproven rather than broken.
      </Alert>
    );
  }

  if (position.granted && position.licensed) return null;

  const selfGrant = (
    <Button
      size="small"
      variant="contained"
      color="warning"
      disableElevation
      disabled={granting}
      onClick={onSelfGrant}
    >
      {email ? `Grant access to ${email}` : 'Grant access to the signed-in account'}
    </Button>
  );

  if (position.granted && !position.licensed) {
    return (
      <Alert severity="warning" sx={{ mt: 2 }}>
        <AlertTitle>Download will fail — no licence</AlertTitle>
        This account is on the access list, but it holds no licence for{' '}
        <strong>{application}</strong>, and a download needs both. A licence is not
        something this screen can grant: it has to be added to the account.
      </Alert>
    );
  }

  if (!position.granted && position.licensed) {
    return (
      <Alert severity="warning" sx={{ mt: 2 }} action={selfGrant}>
        <AlertTitle>Download will fail — not on the access list</AlertTitle>
        This account is licensed for <strong>{application}</strong> but has not been
        granted access to this package. That is fixable from here.
      </Alert>
    );
  }

  return (
    <Alert severity="warning" sx={{ mt: 2 }} action={selfGrant}>
      <AlertTitle>Download will fail — no licence and no grant</AlertTitle>
      This account is neither on the access list nor licensed for{' '}
      <strong>{application}</strong>. Granting access here fixes one half; the licence
      has to be added to the account separately, and until it is, the grant stays inert.
    </Alert>
  );
};

const PackageSummary = (props) => {
  const {
    application,
    pkg,
    thumbUri,
    iconUri,
    busy,
    editing,
    onEdit,
    onPublish,
    onUnpublish,
    onVerify,
    onDownload,
    onDelete,
    selfPosition,
    selfEmail,
    selfGranting,
    onSelfGrant,
  } = props;

  const links = pkg.__links || {};
  const status = packageStatus(pkg);

  return (
    <Card>
      <CardContent>
        <Link component={RouterLink} to={`/packages/${application}`} variant="body2">
          ← All packages
        </Link>

        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={3}
          sx={{ mt: 1, alignItems: { md: 'flex-start' } }}
        >
          <Box sx={{ flex: '1 1 auto' }}>
            <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
              <Typography variant="h3">{pkg.name}</Typography>
              <Typography variant="h5" color="text.secondary">
                {pkg.version}
              </Typography>
              <Chip
                label={status}
                color={packageStatusColor(status)}
                variant={status === 'Draft' ? 'outlined' : 'filled'}
              />
            </Stack>

            {/* Publish, unpublish, verify and download follow link presence — a
                missing link means the action is unavailable. Edit and delete have
                no link of their own and are gated by the manager check that let
                this page render. */}
            <Stack direction="row" sx={{ mt: 2, flexWrap: 'wrap', gap: 1 }}>
              {links.publish && (
                <Button
                  variant="contained"
                  disableElevation
                  startIcon={<PublishIcon />}
                  disabled={busy}
                  onClick={onPublish}
                >
                  Publish
                </Button>
              )}
              {links.unpublish && (
                <Button
                  variant="outlined"
                  startIcon={<UnpublishedIcon />}
                  disabled={busy}
                  onClick={onUnpublish}
                >
                  Unpublish
                </Button>
              )}
              <Button
                variant={editing ? 'contained' : 'outlined'}
                startIcon={<EditIcon />}
                disabled={busy}
                onClick={onEdit}
              >
                {editing ? 'Stop editing' : 'Edit'}
              </Button>
              {links.verify && (
                <Button variant="outlined" startIcon={<FactCheckIcon />} onClick={onVerify}>
                  Verify
                </Button>
              )}
              {links.download && (
                <Button variant="outlined" startIcon={<DownloadIcon />} onClick={onDownload}>
                  Download
                </Button>
              )}
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteOutlineIcon />}
                disabled={busy}
                onClick={onDelete}
              >
                Delete
              </Button>
            </Stack>

            {/* Download stays visible even when it will fail — a manager needs to
                know it exists and why it does not work. */}
            {!pkg.is_public && (
              <DownloadDiagnosis
                application={application}
                position={selfPosition}
                email={selfEmail}
                granting={selfGranting}
                onSelfGrant={onSelfGrant}
              />
            )}
          </Box>

          <Stack direction="row" spacing={2}>
            <Preview label="Icon" uri={iconUri} />
            <Preview label="Thumbnail" uri={thumbUri} />
          </Stack>
        </Stack>
      </CardContent>

      <Divider />

      <CardContent>
        <Grid container spacing={2}>
          <Field label="Type">{pkg.type}</Field>
          <Field label="File name">{pkg.file_name}</Field>
          <Field label="Size">{formatSize(pkg.size)}</Field>
          <Field label="Expanded size">{formatSize(pkg.expanded_size)}</Field>
          <Field label="Label">{pkg.label}</Field>
          <Field label="Public">
            {pkg.is_public ? 'Yes — anyone can download' : 'No — the access list decides'}
          </Field>
          <Field label="Created">{formatDateTime(pkg.created_at)}</Field>
          <Field label="Updated">{formatDateTime(pkg.updated_at)}</Field>
          <Field label="Updated by">{pkg.updated_by}</Field>
          <Field label="UUID">{pkg.uuid}</Field>
        </Grid>
      </CardContent>
    </Card>
  );
};

PackageSummary.propTypes = {
  application: PropTypes.string.isRequired,
  busy: PropTypes.bool,
  editing: PropTypes.bool,
  iconUri: PropTypes.string,
  onDelete: PropTypes.func.isRequired,
  onDownload: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onPublish: PropTypes.func.isRequired,
  onSelfGrant: PropTypes.func,
  onUnpublish: PropTypes.func.isRequired,
  onVerify: PropTypes.func.isRequired,
  pkg: PropTypes.object.isRequired,
  selfEmail: PropTypes.string,
  selfGranting: PropTypes.bool,
  selfPosition: PropTypes.object,
  thumbUri: PropTypes.string,
};

export default PackageSummary;
